import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { userAPI, friendshipAPI } from '../api';
import api from '../api';
import { AuthContext } from '../AuthContext';
import { t } from '../i18n';

const resolvePhotoUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const base = (api && api.defaults && api.defaults.baseURL) ? api.defaults.baseURL : '';
  const origin = base.replace(/\/api\/?$/, '');
  if (url.startsWith('/')) return `${origin}${url}`;
  return `${origin}/${url}`;
};

const PublicProfile = () => {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [friendshipId, setFriendshipId] = useState(null);
  const [actionMessage] = useState('');
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showResultModal, setShowResultModal] = useState({ open: false, message: '', isError: false });
  const navigate = useNavigate();
  const { user: currentUser } = useContext(AuthContext);

  useEffect(() => {
    if (currentUser && username === currentUser.username) {
      // If visiting your own username route, redirect to canonical `/profile`
      navigate('/profile');
      return;
    }

    const load = async () => {
      try {
        const res = await userAPI.getByUsername(username);
        setProfile(res.data);
      } catch (err) {
        setError(t('userNotFound'));
      } finally {
        setLoading(false);
      }
    };
    load();
    // also check whether this user is already a friend so we can show remove button
    const checkFriendship = async () => {
      try {
        const res = await friendshipAPI.getMyFriends();
        const fs = res.data.find(f => f.friend && f.friend.username === username);
        if (fs) {
          setFriendshipId(fs.id);
        } else {
          // not friends — redirect to the invite page
          navigate(`/invite/${username}`);
        }
      } catch (err) {
        // on error, redirect to invite as fallback
        navigate(`/invite/${username}`);
      }
    };
    checkFriendship();
  }, [username, navigate]);

  if (loading) return <div>{t('loading')}</div>;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;

  return (
    <div className="container profile-page">
      <h1>{profile.username}</h1>
      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
        <div>
          {profile.profile_photo ? (
            <img className="profile-photo" src={resolvePhotoUrl(profile.profile_photo)} alt={profile.username} />
          ) : (
            <div style={{ width: 120, height: 120, borderRadius: '50%', background: '#ddd' }} />
          )}
        </div>
        <div>
          <p><strong>{t('name')}:</strong> {profile.first_name || profile.last_name ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : t('notSet')}</p>
          <p><strong>{t('description')}:</strong> {profile.description || t('notSet')}</p>
          <p><strong>{t('whereToContact')}:</strong></p>
          <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{profile.contact_info || t('notSet')}</pre>
          {currentUser && currentUser.username !== profile.username && (
            <div style={{ marginTop: 12 }}>
              {friendshipId ? (
                <>
                  <button className="btn btn-danger" onClick={() => setShowRemoveModal(true)}>{t('removeFriendship')}</button>

                  {showRemoveModal && (
                    <div className="crop-modal" role="dialog" aria-modal="true" aria-labelledby="remove-title">
                      <div className="crop-backdrop" onClick={() => setShowRemoveModal(false)} />
                      <div className="crop-panel" style={{ maxWidth: 560 }}>
                        <h3 id="remove-title">{t('confirmRemoveFriend')}</h3>
                        <p>{t('confirmRemoveFriendBefore')} {profile.username} {t('confirmRemoveFriendAfter')}</p>
                        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                          <button
                            className="btn btn-danger"
                            disabled={isRemoving}
                            onClick={async () => {
                              setIsRemoving(true);
                              try {
                                await friendshipAPI.removeFriend(friendshipId);
                                setShowRemoveModal(false);
                                setFriendshipId(null);
                                setShowResultModal({ open: true, message: t('friendRemoved'), isError: false });
                                setIsRemoving(false);
                                setTimeout(() => navigate('/friends'), 900);
                              } catch (err) {
                                setShowRemoveModal(false);
                                setShowResultModal({ open: true, message: t('failedToRemoveFriend'), isError: true });
                                setIsRemoving(false);
                              }
                            }}
                          >
                            {t('confirmRemove')}
                          </button>
                          <button className="btn btn-secondary" onClick={() => setShowRemoveModal(false)} disabled={isRemoving}>{t('back')}</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {showResultModal.open && (
                    <div className="crop-modal" role="dialog" aria-modal="true" aria-labelledby="result-title">
                      <div className="crop-backdrop" onClick={() => setShowResultModal({ open: false, message: '', isError: false })} />
                      <div className="crop-panel" style={{ maxWidth: 560 }}>
                        <h3 id="result-title">{showResultModal.isError ? t('error') : t('removed')}</h3>
                        <p>{showResultModal.message}</p>
                        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                          <button className="btn btn-primary" onClick={() => { setShowResultModal({ open: false, message: '', isError: false }); if (!friendshipId) navigate('/friends'); }}>{t('ok')}</button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : null}
              {actionMessage && <div style={{ marginTop: 8, color: 'green' }}>{actionMessage}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicProfile;
